import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import ScanBadge from './ScanBadge';

const ITEMS_PER_PAGE = 5;

const SkillProgressCard = ({ groupedNextActions = [] }) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(groupedNextActions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = groupedNextActions.slice(startIndex, endIndex);

  // Reset to page 1 if items change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [groupedNextActions.length]);

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-card-foreground">Skill Development</h3>
        <Icon name="TrendingUp" size={20} className="text-primary" />
      </div>

      {(!groupedNextActions || groupedNextActions.length === 0) ? (
        <div className="text-sm text-muted-foreground">
          No actionable items yet. Run a live analysis to get your personalized learning steps.
        </div>
      ) : (
        <div className="space-y-5">
          {currentItems
            .filter(section => section.items && section.items.length > 0) // Don't show empty sections
            .map((section) => (
              <div key={section.id} className="border border-border rounded-lg">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-foreground">{section.title}</div>
                      <ScanBadge scanNumber={section.scanNumber} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {section.created_at ? new Date(section.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{section.items?.length || 0} actions</div>
                </div>

                <div className="p-4 space-y-3">
                  {section.items.map((a, idx) => (
                    <div key={`${section.id}-${idx}`} className="flex items-start justify-between gap-3 bg-muted/30 rounded-md px-3 py-2">
                      <div>
                        <div className="text-xs text-muted-foreground mb-0.5">{a.skill}</div>
                        <div className="text-sm font-medium text-foreground">{a.type}: {a.title || 'Recommended resource'}</div>
                        {a.provider && <div className="text-xs text-muted-foreground mt-0.5">{a.provider}</div>}
                      </div>
                      {a.url && (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 rounded-md border border-border hover:bg-muted transition-colors"
                        >
                          Open
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Pagination Controls */}
      {groupedNextActions && groupedNextActions.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md text-sm border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md text-sm border transition-colors ${currentPage === page
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
                }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md text-sm border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SkillProgressCard;